import { useRef, useState } from 'react'
import { Alert, Box, Button, Stack } from '@mui/material'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import QrScannerDialog from '../../../generic/qr/QrScannerDialog'
import { DOCUMENT_SCOPES } from '../../../generic/qr/qrDocumentResources'
import { parseQrPayload } from '../../../generic/qr/qrPayload'
import useQrInput from '../../../generic/qr/useQrInput'
import useMosInfo from '../common/provider/useMosInfo'
import JobDetailMos from '../FabricationOrderJobReference/PlannedJobs/components/JobDetail/JobDetailMos'
import { JOB_MATCH_TYPES, QR_ACTIONS, QR_CONTEXT_MATCHES, QR_MATCH_TYPES } from './qrLookupApi'
import { readQrLookupConfig } from './qrLookupConfig'
import { buildDocumentTree, flattenTree } from './qrDocumentTree'
import QrDisambiguationDialog from './QrDisambiguationDialog'
import QrLastReadCard from './QrLastReadCard'
import QrLookupResultDialog from './QrLookupResultDialog'
import QrNotPlannedHereNotice from './QrNotPlannedHereNotice'
import QrScanTarget from './QrScanTarget'
import QrTargetDeviceDialog from './QrTargetDeviceDialog'
import { jobLabel } from './qrJobText'
import useQrLookup from './useQrLookup'

/**
 * Scans a MIC label and opens what it points at.
 *
 * One entry point, on purpose. The card of a scanned element already opens on the level the station
 * configured — REFERENCE by default, which is where the drawings live — so a second "documentation"
 * button led to the same documents with the field grid hidden: a view preference dressed up as a
 * different question, and two buttons that both mean "scan something" are one too many at a machine.
 * Which level opens first is still the station's call through `initialDocumentScope`.
 *
 * Unlike the QR Reader widget — which only reads and shows the payload — this one acts: every read
 * goes to Sun, which resolves the element, decides whether it belongs to the station's active work
 * and records the scan. That includes the reads it rejects, which is exactly what makes the audit
 * trail worth having: a wrong label scanned at a machine is the event you want to find later.
 *
 * Shape of the widget: a state panel that fills whatever height the station gave it, and the entry
 * point as a touch target pinned below it. The panel is the viewfinder until something is read and the
 * card of that read afterwards — one state at a time, because the widget is a foot away from a spindle
 * and glanced at, not studied.
 */

// The dialog rejects an unusable label by keeping itself open with the message we return, so the
// expected-type check is NOT delegated to it: doing that would reject the label client-side and Sun
// would never learn the scan happened.
const SAMPLE_RESULT = {
  outcome: 'RESOLVED',
  entityType: 'RawMaterial',
  entityId: 412,
  title: 'RM-000412',
  subtitle: 'AL7075-T6',
  header: [{ label: 'Batch', value: '8842' }, { label: 'Available', value: '2.0' }],
  documentSources: [
    { scope: DOCUMENT_SCOPES.INSTANCE, resourceType: 'RAW_MATERIAL', referenceId: 412, name: 'RM-000412', documentCount: 1 },
    { scope: DOCUMENT_SCOPES.REFERENCE, resourceType: 'RAW_MATERIAL_REFERENCE', referenceId: 57, name: 'AL7075-T6', documentCount: 2 }
  ],
  orderLines: [],
  contextMatch: QR_CONTEXT_MATCHES.ACTIVE,
  traceId: 0,
  at: ''
}

const VIEWS = { NONE: '', SHEET: 'sheet', JOB: 'job', TARGET: 'target', CANDIDATES: 'candidates' }

// "View FO card" opens the sheet of the order, always (RN17): the read itself when the order was read,
// the sheet Sun built for it when a part was read — {} when Sun could not build one.
const orderSheet = (read) => read.entityType === 'FabricationOrder' ? read : read.fabricationOrderSheet

// JobDetailMos takes the order in the shape of the planned-jobs list, `fabricationOrder` being the
// code: its status-change dialog prints that field. Sun's DTO calls it `code` (RN15).
const jobDetailOrder = (order) => ({ id: order.id, fabricationOrder: order.code, description: order.description })

const SCOPE_BY_CONFIG = { Reference: DOCUMENT_SCOPES.REFERENCE, Instance: DOCUMENT_SCOPES.INSTANCE }

// Whether the card speaks for a Job detail read of an order or a part (RN27): Sun resolved it to a
// job, a list or a machine to go to. A sheet — whatever the action — keeps today's card, and so does a
// list left with no job to pick, which opens the sheet too.
const isJobRead = (action, read) =>
  action === QR_ACTIONS.JOB_DETAIL && Boolean(read.matchType) && read.matchType !== QR_MATCH_TYPES.NOT_APPLICABLE &&
  !(read.matchType === QR_MATCH_TYPES.MULTIPLE_CANDIDATES && read.matchingJobs.length === 0)

// What the read did, in the card's words (RN27).
const cardLines = (read, t_) => {
  if (JOB_MATCH_TYPES.includes(read.matchType)) {
    const opened = { text: t_('Opened {{job}}', { job: jobLabel(read.job) }), tone: 'default' }
    return read.matchType === QR_MATCH_TYPES.TECHNOLOGY_PLANNED
      ? [opened, { text: t_('Compatible by technology · not planned here'), tone: 'warning' }]
      : [opened]
  }
  if (read.matchType === QR_MATCH_TYPES.MULTIPLE_CANDIDATES) {
    return [{ text: t_('{{count}} jobs this machine can do', { count: read.matchingJobs.length }), tone: 'default' }]
  }
  if (read.targetDevice?.id) {
    return [{ text: t_('No work here · next at {{machine}}', { machine: read.targetDevice.name }), tone: 'warning' }]
  }
  return [{ text: t_('No work here'), tone: 'warning' }]
}

// A grid cell is 80px, so two cells hold ~160px and the widget header eats a third of that: barely
// enough for one row of controls. A size of 0 means nobody sized the widget — no constraint at all.
const isShort = (heightInCells) => heightInCells > 0 && heightInCells <= 2

// A button an operator wearing gloves can hit, and the only control in the widget: it takes the whole
// width and keeps its height everywhere but in a widget with no height to give.
const actionSx = (short) => ({
  flex: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  minHeight: short ? '2.25rem' : '2.75rem',
  px: 1.5,
  borderRadius: '0.75rem',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: short ? '0.82rem' : '0.9rem'
})

const QrLookupWidget = ({ data = {}, preview = false, w = 0, h = 0 }) => {
  const { t_ } = useSmartTranslation()
  const { device, worker, fabricationOrder, navigateToDeviceId } = useMosInfo()

  const config = readQrLookupConfig(data)
  const expectedEntityType = config.expectedEntityType
  const allowCamera = config.allowCamera !== false
  const allowManual = config.allowManual !== false
  const preferredScope = SCOPE_BY_CONFIG[config.initialDocumentScope] ?? DOCUMENT_SCOPES.REFERENCE

  const { scan, lastResult, pending, cancelPending } = useQrLookup({
    deviceId: device?.id ?? 0,
    workerId: worker?.id ?? 0,
    expectedEntityType,
    action: config.action,
    // A whitelist on purpose: suggestTargetDevice and listenWithoutDialog are the front's own, and a
    // prioritizeActive left by an older widget is not part of the contract any more (S76).
    jobPolicies: {
      // Sent as saved: Sun reads anything but PRIORITY as PLAN, and a missing key is PLAN already.
      jobOrderCriterion: config.jobOrderCriterion,
      allowTechnologyFallback: config.allowTechnologyFallback !== false,
      askOperatorOnMultipleJobs: config.askOperatorOnMultipleJobs === true
    }
  })

  const [scanning, setScanning] = useState(false)
  // Which opening of the scan dialog we are in: it moves whenever the dialog opens or closes, so an
  // answer that comes back after the operator closed it can tell and open nothing.
  const dialogSession = useRef(0)
  // Which of the widget's own windows the last read opened; one at a time.
  const [view, setView] = useState(VIEWS.NONE)
  const [jobId, setJobId] = useState(0)
  // The sheet the SHEET view shows: the read itself, or the order sheet "View FO card" picked.
  const [sheet, setSheet] = useState({})
  // Why the last reader burst was refused; '' once a read is accepted. Only one at a time (RN21).
  const [readerError, setReaderError] = useState('')
  // The notice over the job detail when the job opened is not planned here (RN28).
  const [notPlannedHere, setNotPlannedHere] = useState({ open: false, plannedAt: '' })

  const result = preview ? SAMPLE_RESULT : lastResult
  const hasResult = Boolean(result?.entityType)
  // For an order or a part, FOREIGN says "not active on this machine", which is exactly what the read
  // just dealt with by opening its job here or saying where its work is: the card drops the flag.
  const jobRead = hasResult && isJobRead(config.action, result)

  // How the widget was sized decides how much of itself it shows. The preview is rendered in a 20rem
  // box with h=1, so its size says nothing about the room it has: it always gets the full layout.
  const short = !preview && isShort(Number(h))

  // A job opened only because this machine can do it gets the notice of RN28, naming the machine the
  // plan has it at when Sun knows one other than this.
  const openJob = (job, warnIfNotPlanned) => {
    setJobId(job.jobReferenceId)
    setView(VIEWS.JOB)
    // A notice of an earlier job must not outlive it: every opening starts without one.
    setNotPlannedHere({ open: false, plannedAt: '' })
    if (!warnIfNotPlanned || job.matchType !== QR_MATCH_TYPES.TECHNOLOGY_PLANNED) return
    const plannedElsewhere = Boolean(job.plannedDevice.id) && job.plannedDevice.id !== device?.id
    setNotPlannedHere({ open: true, plannedAt: plannedElsewhere ? job.plannedDevice.name : '' })
  }

  // What a read opens follows what Sun resolved (RN15): a single job opens its detail, anything else
  // the element's sheet. Reopened from the card it opens the same, without the notice of RN28: the
  // card already says the job is not planned here.
  const open = (read, isNewRead = false) => {
    setReaderError('')
    if (JOB_MATCH_TYPES.includes(read.matchType)) {
      // The answer's own matchType: the job of a single resolution carries it too, but an older Sun
      // may not send it there.
      openJob({ ...read.job, matchType: read.matchType }, isNewRead)
      return
    }
    if (read.matchType === QR_MATCH_TYPES.MULTIPLE_CANDIDATES) {
      // Candidates that did not survive the reading leave nothing to pick from: the sheet it is.
      if (read.matchingJobs.length === 0) {
        openSheet(read)
        return
      }
      setView(VIEWS.CANDIDATES)
      return
    }
    if (read.matchType === QR_MATCH_TYPES.NO_JOB_FOR_DEVICE) {
      setView(VIEWS.TARGET)
      return
    }
    openSheet(read)
  }

  const openSheet = (card) => {
    setSheet(card)
    setView(VIEWS.SHEET)
  }

  const closeScanDialog = () => {
    dialogSession.current += 1
    setScanning(false)
  }

  const handleScan = async (parsed) => {
    const session = dialogSession.current
    const { message, result: read, stale } = await scan({ parsed })
    if (stale) return ''
    // Closed meanwhile: the operator walked away from that read, so its answer opens nothing.
    if (session !== dialogSession.current) return ''
    if (message) return message

    closeScanDialog()
    open(read, true)
  }

  // The reader with the dialog closed (RN21): the same read, sent as SCANNER, with no dialog first.
  // Off while any window of the widget is open, so a burst never lands behind one (RN22), and while a
  // read is in flight, so two reads in a row never race each other.
  const handleReaderScan = async (raw) => {
    const { message, result: read, stale } = await scan({ parsed: { ...parseQrPayload(raw), source: 'scanner' } })
    if (stale) return
    // No dialog to keep open here, so the refusal is said on the widget itself, as the QR Reader does.
    if (message) {
      setReaderError(message)
      return
    }
    open(read, true)
  }

  useQrInput({
    enabled: config.listenWithoutDialog === true && !preview && !scanning && !pending && view === VIEWS.NONE,
    onScan: handleReaderScan
  })

  // Every window of the widget is modal and resets the view when it closes, so Scan is only reachable
  // with the view already at NONE.
  // The dialog on screen closes only itself: a late onClose of an earlier one must not close this one.
  const openedSession = dialogSession.current

  const startScan = () => {
    setReaderError('')
    // A direct read still in flight must not open its answer behind the dialog.
    cancelPending()
    dialogSession.current += 1
    setScanning(true)
  }

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minHeight: 0, minWidth: 0 }}>
        {hasResult
          ? (
            <QrLastReadCard
              entityType={result.entityType}
              title={result.title}
              at={result.at ?? ''}
              documentCount={documentCount(result)}
              isForeign={!jobRead && result.contextMatch === QR_CONTEXT_MATCHES.FOREIGN}
              interactive={!preview}
              compact={short}
              lines={jobRead ? cardLines(result, t_) : []}
              onOpen={() => open(result)}
            />
            )
          : <QrScanTarget framed={!short} action={config.action} />}
      </Box>

      {readerError !== '' && (
        <Alert severity='error' sx={{ flexShrink: 0, py: 0 }}>{readerError}</Alert>
      )}

      <Stack direction='row' sx={{ flexShrink: 0 }}>
        <Button
          variant='contained'
          startIcon={<QrCodeScannerRoundedIcon />}
          onClick={startScan}
          disabled={preview}
          sx={actionSx(short)}
        >
          {t_('Scan')}
        </Button>
      </Stack>

      {scanning && (
        <QrScannerDialog
          open
          onClose={() => { if (dialogSession.current === openedSession) closeScanDialog() }}
          onScan={handleScan}
          title='Scan QR code'
          expectedEntityTypes={[]}
          allowCamera={allowCamera}
          allowManual={allowManual}
        />
      )}

      {view === VIEWS.SHEET && (
        <QrLookupResultDialog
          open
          result={sheet}
          preferredScope={preferredScope}
          activeFabricationOrderCode={fabricationOrder?.fabricationOrder ?? ''}
          onClose={() => setView(VIEWS.NONE)}
        />
      )}

      {view === VIEWS.TARGET && (
        <QrTargetDeviceDialog
          open
          machineName={device?.name ?? ''}
          orderCode={result.fabricationOrder.code ?? ''}
          partCode={result.entityType === 'Part' ? result.title : ''}
          targetDevice={result.targetDevice}
          pendingJob={result.pendingJob}
          pendingWorkLookup={result.pendingWorkLookup}
          offerGoToMachine={config.suggestTargetDevice !== false}
          canViewSheet={Boolean(orderSheet(result).entityType)}
          onGoToMachine={(deviceId) => {
            setView(VIEWS.NONE)
            navigateToDeviceId(deviceId)
          }}
          onViewSheet={() => openSheet(orderSheet(result))}
          onClose={() => setView(VIEWS.NONE)}
        />
      )}

      {view === VIEWS.CANDIDATES && (
        <QrDisambiguationDialog
          open
          orderCode={result.fabricationOrder.code ?? ''}
          jobs={result.matchingJobs}
          jobOrderCriterion={config.jobOrderCriterion}
          onSelect={(job) => openJob(job, true)}
          onClose={() => setView(VIEWS.NONE)}
        />
      )}

      {view === VIEWS.JOB && (
        <JobDetailMos
          open
          fabricationOrder={jobDetailOrder(result.fabricationOrder)}
          initialJob={jobId}
          setOpen={(isOpen) => {
            if (isOpen) return
            setView(VIEWS.NONE)
            setNotPlannedHere({ open: false, plannedAt: '' })
          }}
        />
      )}

      <QrNotPlannedHereNotice
        open={view === VIEWS.JOB && notPlannedHere.open}
        plannedAt={notPlannedHere.plannedAt}
        onClose={() => setNotPlannedHere({ open: false, plannedAt: '' })}
      />
    </Box>
  )
}

// Anywhere in what was resolved, structure included: an order can hold every drawing in its
// fabrication orders and none of its own, and counting only its own would read as "nothing here".
const documentNodes = (result) =>
  flattenTree(buildDocumentTree(result?.documentSources ?? [], result?.orderLines ?? []))

const documentCount = (result) =>
  documentNodes(result).reduce((total, node) => total + node.documentCount, 0)

export default QrLookupWidget
